import {
  InfiniteData,
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
} from '@tanstack/react-query'
import { ExpenseReportsFiltersBody } from 'src/components/ExpenseReportsBox/ExpenseReportsBox'
import { ExpenseReportModel } from 'src/models/expenseReport.model'
import {
  ExpenseReportsListDataModel,
  ExpenseReportsSuccessPayload,
} from 'src/queries/expenseReports/expenseReports.models'
import {
  getApproverExpenseReports,
  getDirectReportsExpenseReports,
  getExpenseReportDetail,
  getSubmitterExpenseReports,
  getSubmitterUsers,
} from 'src/queries/expenseReports/expenseReports.network'
import { oopeQueryKeys } from 'src/queries/queries.constants'
import { getNextExtendPageParam } from 'src/queries/queries.utils'

const OOPE_SUBMITTER_USERS_QK = 'submitterusers'

const DEFAULT_PER_PAGE = 50

/**
 * Flattens the paginated pages into the model shape FullExpenseReportTable
 * reads. Identical to the legacy useExtendInfiniteQuery `select`, so the swap
 * is consumer-transparent.
 */
export const selectExpenseReportsListData = (
  response: InfiniteData<ExpenseReportsSuccessPayload, number>,
): ExpenseReportsListDataModel => ({
  expenseReports: response?.pages.flatMap((page) =>
    page.expenseReports.map((expReport) => new ExpenseReportModel(expReport)),
  ),
  page: response?.pages[0]?.pagination.page,
  numberOfPages: response?.pages[0]?.pagination.numberOfPages,
})

/**
 * Keys delegate to the shared oopeQueryKeys factory so they stay byte-identical
 * to the entries the legacy useQuery*OOPEs hooks wrote; the swap never
 * double-fetches or strands a cache entry.
 */
export const expenseReportsQueryKeys = {
  submitterList: (filters: ExpenseReportsFiltersBody | undefined) =>
    oopeQueryKeys.submitterList(filters),
  approverList: (filters: ExpenseReportsFiltersBody | undefined) =>
    oopeQueryKeys.approverList(filters),
  directReportsList: (filters: ExpenseReportsFiltersBody | undefined) =>
    oopeQueryKeys.directReportsList(filters),
  /**
   * Delegates to the shared oope factory so the migrated detail read shares one
   * cache entry with the (now-removed) useExtendQuery wrapper's stable key and
   * any cross-feature invalidation that already targets oopeQueryKeys.reportDetails.
   */
  reportDetail: (reportId: string | undefined) =>
    oopeQueryKeys.reportDetails(reportId),
  submitterUsers: () =>
    [...oopeQueryKeys.all, OOPE_SUBMITTER_USERS_QK] as const,
}

const listQuery = (
  queryKey: ReturnType<typeof oopeQueryKeys.submitterList>,
  queryFn: (params: {
    filters: ExpenseReportsFiltersBody | undefined
    page: number
    perPage: number
  }) => Promise<ExpenseReportsSuccessPayload>,
  filters: ExpenseReportsFiltersBody | undefined,
  perPage: number,
) =>
  infiniteQueryOptions({
    queryKey,
    queryFn: ({ pageParam }) => queryFn({ filters, page: pageParam, perPage }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => getNextExtendPageParam(lastPage.pagination),
    select: selectExpenseReportsListData,
    /**
     * keepPreviousData keeps prior pages visible while the next page loads on
     * infinite scroll; preserves the legacy wrapper's no-flash behavior.
     */
    placeholderData: keepPreviousData,
    /**
     * Matches the legacy useExtendInfiniteQuery default (queries.utils.ts): with
     * staleTime 0 + the global refetchOnMount predicate, a non-zero gcTime makes
     * React Query replay every cached page when the user navigates away and back
     * mid-scroll (page 10 → leave → return → 10 sequential refetches). gcTime: 0
     * drops the cache on unmount so a return starts fresh from page 0.
     */
    gcTime: 0,
  })

export const expenseReportsQueries = {
  submitterList: (
    filters: ExpenseReportsFiltersBody | undefined,
    perPage: number = DEFAULT_PER_PAGE,
  ) =>
    listQuery(
      expenseReportsQueryKeys.submitterList(filters),
      getSubmitterExpenseReports,
      filters,
      perPage,
    ),
  approverList: (
    filters: ExpenseReportsFiltersBody | undefined,
    perPage: number = DEFAULT_PER_PAGE,
  ) =>
    listQuery(
      expenseReportsQueryKeys.approverList(filters),
      getApproverExpenseReports,
      filters,
      perPage,
    ),
  directReportsList: (
    filters: ExpenseReportsFiltersBody | undefined,
    perPage: number = DEFAULT_PER_PAGE,
  ) =>
    listQuery(
      expenseReportsQueryKeys.directReportsList(filters),
      getDirectReportsExpenseReports,
      filters,
      perPage,
    ),
  /**
   * OOPE report detail. enabled-guards on reportId so an undefined route param
   * never builds /expensereports/undefined (matches the legacy wrapper's
   * `enabled: reportId !== undefined`). retry:false preserves the legacy
   * `getExpenseReport` thunk's single-attempt behavior; consumers gate a
   * failed read straight to NotFound, so a 404 must surface immediately rather
   * than retrying the global-default 3 times.
   */
  reportDetail: (reportId: string | undefined) =>
    queryOptions({
      queryKey: expenseReportsQueryKeys.reportDetail(reportId),
      queryFn: () => getExpenseReportDetail(reportId!),
      enabled: reportId !== undefined,
      retry: false,
    }),
  /**
   * Submitter users for the reimbursements filter dropdown. retry:false matches
   * the legacy `getSubmitterUsers` thunk's single attempt (consistent with the
   * reportDetail/expense factories); the dropdown gates its empty/loaded state
   * on `!isPending`, so a failed fetch should settle promptly rather than
   * retrying the global-default 3 times.
   */
  submitterUsers: () =>
    queryOptions({
      queryKey: expenseReportsQueryKeys.submitterUsers(),
      queryFn: getSubmitterUsers,
      retry: false,
    }),
}

/**
 * Picks the right list factory for the page context. 'manager' = the
 * direct-reports (needs-approval) list. Shared by FullExpenseReportTable (rows)
 * and ExpenseReportsBox (selection id->model lookup) so both read one cache
 * entry by key.
 */
export const expenseReportsListQueryByContext = (
  context: 'submitter' | 'approver' | 'manager',
  filters: ExpenseReportsFiltersBody | undefined,
) =>
  context === 'approver'
    ? expenseReportsQueries.approverList(filters)
    : context === 'submitter'
      ? expenseReportsQueries.submitterList(filters)
      : expenseReportsQueries.directReportsList(filters)
